import React from 'react';
import PropTypes from 'prop-types';
import ReactCountryFlag from 'react-country-flag';

import { ResponsiveBar } from '@nivo/bar';
import numeral from 'numeral';
import { countryNameToCode } from '../../../utils/countries';

import '../Charts.css';

const CountryDemo = ({
  maxValue,
  query: { loading, data },
  keys,
  indexKey,
  valueKey,
  showSize = true
}) => {
  if (loading) {
    return (
      <div className="tvt-time-chart__loading-state-wrapper country-loading-chart">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="368"
          height="644"
          viewBox="0 0 368 644"
        >
          <defs>
            <linearGradient id="vb0tohwrxa" x1="0%" x2="100%" y1="50%" y2="50%">
              <stop offset="0%" stopColor="#EEEEEE" />
              <stop offset="33%" stopColor="#EEEEEE" />
              <stop offset="50%" stopColor="#f5f5f5" />
              <stop offset="67%" stopColor="#EEEEEE" />
              <stop offset="100%" stopColor="#EEEEEE" />
              <animateTransform
                attributeName="gradientTransform"
                type="translate"
                from="-1 0"
                to="1 0"
                begin="0s"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </linearGradient>
          </defs>
          <path
            fill="url(#vb0tohwrxa)"
            fillRule="evenodd"
            d="M237 10v654h-1v8h-85v-36h85V448h-37v-36h37v-76h-85v-36h85V112h-37V76h37V10h1zm97 0v10h19v36h-19v76h27v36h-27v20h125v36H334v20h185v36H334v76h60v36h-60v76h27v36h-27v20h125v36H334v20h185v36H334v48h-1V10h1zm-30 625v21h-38v-21h38zm17-31v14h-72v-14h72zm-17-25v21h-38v-21h38zm17-31v14h-72v-14h72zm-17-25v21h-38v-21h38zm17-31v14h-72v-14h72zm-17-25v21h-38v-21h38zm17-31v14h-72v-14h72zm-17-25v21h-38v-21h38zm17-30v14h-72v-14h72zm-17-25v21h-38v-21h38zm17-32v14h-72v-14h72zm-17-25v21h-38v-21h38zm17-31v14h-72v-14h72zm-17-25v21h-38v-21h38zm17-31v14h-72v-14h72zm-17-25v21h-38v-21h38zm17-31v14h-72v-14h72zm-17-25v21h-38v-21h38zm17-31v14h-72v-14h72zm-17-25v21h-38V75h38zm17-31v14h-72V44h72zm-17-25v21h-38V19h38z"
            transform="translate(-151 -10)"
          />
        </svg>
      </div>
    );
  }
  const containerHeight = data.length * 56;
  const chartData = data.slice(0).reverse();

  const sampleSize = d =>
    d.reduce((accum, current) => (accum += current.viewers), 0);

  // eslint-disable-next-line react/prop-types
  const RightBarComponent = ({ x, y, width, height, color, label }) => {
    return (
      <g transform={`translate(${x}, ${y})`}>
        <rect
          width={width}
          height={height}
          fill={color}
          strokeWidth="5"
          stroke="#fff"
        />

        <text
          x={width + 15}
          y={height / 2}
          textAnchor="middle"
          dominantBaseline="central"
        >
          {label}
        </text>
      </g>
    );
  };

  return (
    <div
      style={{
        height: containerHeight,
        position: 'relative',
        width: '100%',
        display: 'flex',
        padding: '20px'
      }}
    >
      {showSize && (
        <div className="tvt-chart__sample-size-num">
          {`N = ${numeral(sampleSize(data)).format(',')}`}
        </div>
      )}
      <ResponsiveBar
        enableLabel
        labelFormat={d => {
          if (d === 0) {
            return null;
          }
          return (
            <tspan x={-15} className="user-demo__country-negative-bar">
              {numeral(-1 * d).format('0.00')}
            </tspan>
          );
        }}
        labelTextColor="#373737"
        isInteractive={false}
        height={containerHeight}
        axisLeft={null}
        axisRight={null}
        margin={{
          top: 0,
          right: 0,
          bottom: 0,
          left: 20
        }}
        padding={0.0}
        borderWidth={5}
        borderColor="#fff"
        colors="#e60000"
        enableGridY={false}
        hideZeroValues={false}
        layout="horizontal"
        reverse
        maxValue={maxValue + maxValue * 0.1}
        data={chartData.map(c => {
          if (c[valueKey] > 0) {
            return {
              [valueKey]: 0,
              [indexKey]: c[indexKey]
            };
          }
          return {
            [valueKey]: Math.abs(c[valueKey]),
            [indexKey]: c[indexKey]
          };
        })}
        indexBy={indexKey}
        keys={keys}
      />
      <div
        className="countries"
        style={{
          display: 'flex',
          height: containerHeight,
          flexDirection: 'column',
          margin: '1% 0'
        }}
      >
        {data.map(c => (
          <div
            key={c[indexKey]}
            className="affinity-chart__country-flag-wrapper"
          >
            <ReactCountryFlag
              svg
              style={{
                width: '35px',
                height: 'auto'
              }}
              countryCode={countryNameToCode[c[indexKey]]}
            />
            <div className="affinity-chart__country-flag-name">
              {c[indexKey]}
            </div>
          </div>
        ))}
      </div>
      <ResponsiveBar
        enableLabel
        barComponent={RightBarComponent}
        labelFormat={d => {
          if (d === 0) {
            return null;
          }
          return (
            <tspan className="user-demo__country-positive-bar">
              {numeral(d).format('0.00')}
            </tspan>
          );
        }}
        isInteractive={false}
        labelTextColor="#373737"
        height={containerHeight}
        axisLeft={null}
        axisRight={null}
        maxValue={maxValue + maxValue * 0.1}
        margin={{
          top: 0,
          right: 20,
          bottom: 0,
          left: 0
        }}
        padding={0.0}
        borderWidth={5}
        borderColor="#fff"
        colors="#66cc00"
        enableGridY={false}
        hideZeroValues={false}
        layout="horizontal"
        data={chartData.map(c => {
          if (c[valueKey] < 0) {
            return {
              [valueKey]: 0,
              [indexKey]: c[indexKey]
            };
          }
          return c;
        })}
        indexBy={indexKey}
        keys={keys}
      />
    </div>
  );
};

CountryDemo.propTypes = {
  query: PropTypes.shape({
    data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    loading: PropTypes.bool.isRequired
  }).isRequired,
  maxValue: PropTypes.number,
  indexKey: PropTypes.string.isRequired,
  valueKey: PropTypes.string.isRequired,
  keys: PropTypes.arrayOf(PropTypes.string).isRequired
};

CountryDemo.defaultProps = {
  maxValue: 5
};

export default CountryDemo;
